import { z } from "zod";
import { ApiClient, ApiError } from "./api-client";
import { addressSchema, challengeSchema, hex32Schema, proofSchema, unsignedDecimalSchema } from "./schemas";

const seconds = z.number().int().nonnegative().safe().max(Math.floor(Number.MAX_SAFE_INTEGER / 1_000));
const generation = z.number().int().positive().safe();
const workerId = z.string().min(8).max(128).regex(/^[A-Za-z0-9_-]+$/);
const nonce = unsignedDecimalSchema.max(78).refine(value => BigInt(value) < (1n << 256n));
const boundary = unsignedDecimalSchema.max(78).refine(value => BigInt(value) <= (1n << 256n));
const target = nonce.refine(value => BigInt(value) > 0n);

// Reuse the full protocol DTOs; session reads must not accept abbreviated handoff examples.
export const sessionChallengeSchema = challengeSchema.extend({
  chainId: z.number().int().positive().safe(), difficultyEpochId: z.number().int().nonnegative().safe(),
  targetExclusive: target, issuedAt: seconds, expiresAt: seconds,
}).refine(value => value.expiresAt > value.issuedAt, "Invalid challenge lifetime");
export const sessionProofSchema = proofSchema.extend({
  nonce, targetExclusive: target, difficultyEpochId: z.number().int().nonnegative().safe(), acceptedAt: seconds, expiresAt: seconds,
}).refine(value => value.expiresAt > value.acceptedAt, "Invalid proof lifetime");

const leaseView = z.discriminatedUnion("state", [
  z.object({ state: z.literal("AVAILABLE"), leaseExpiresAt: z.null() }),
  z.object({ state: z.enum(["OWNED", "IN_USE"]), leaseExpiresAt: seconds }),
]);
const clockFields = { serverTime: seconds, authExpiresAt: seconds };
export const miningCurrentSchema = z.discriminatedUnion("status", [
  z.object({ ...clockFields, status: z.literal("NONE"), challenge: z.null(), proof: z.null(), lease: z.null() }),
  z.object({ ...clockFields, status: z.literal("LEGACY_CONFLICT"),
    challenges: z.array(sessionChallengeSchema).min(2), proof: z.null(), lease: z.null() }),
  z.object({ ...clockFields, status: z.enum(["ACTIVE", "SOLVED", "EXPIRED", "CANCELLED"]),
    challenge: sessionChallengeSchema, proof: sessionProofSchema.nullable(), lease: leaseView.nullable() }),
]).superRefine((value, ctx) => {
  const invalid = (message: string) => ctx.addIssue({ code: "custom", message });
  if (value.status === "NONE") return;
  if (value.status === "LEGACY_CONFLICT") {
    if (value.challenges.some(challenge => challenge.status !== "ACTIVE")) invalid("Conflict contains inactive challenge");
    if (new Set(value.challenges.map(challenge => challenge.challengeId.toLowerCase())).size !== value.challenges.length)
      invalid("Conflict contains duplicate challenges");
    return;
  }
  if (value.challenge.status !== value.status) invalid("Selected challenge status mismatch");
  if (value.proof && !proofMatchesChallenge(value.proof, value.challenge)) invalid("Selected proof mismatch");
  if (value.status === "ACTIVE" && (!value.lease || value.proof)) invalid("Invalid active attempt");
  if (value.status === "SOLVED" && !value.proof) invalid("Solved attempt missing proof");
  // current() can expire an ACTIVE row inside getChallenge(), then construct
  // any structurally valid lease view using its earlier row/time snapshot.
  // EXPIRED is authoritative; the transport boundary below removes that lease.
  if (value.status !== "ACTIVE" && value.status !== "EXPIRED" && value.lease !== null)
    invalid("Terminal attempt has execution lease");
}).transform(value => value.status === "EXPIRED" ? { ...value, lease: null } : value);

export const miningLeaseSchema = z.object({
  generation, leaseExpiresAt: seconds, serverTime: seconds,
  heartbeatEverySeconds: z.number().int().positive().safe(),
}).refine(value => value.leaseExpiresAt > value.serverTime, "Expired lease response");
export const miningRangeSchema = z.object({
  startNonce: nonce, endNonceExclusive: boundary, allocatedAt: seconds, serverTime: seconds,
}).refine(value => BigInt(value.startNonce) < BigInt(value.endNonceExclusive) &&
  value.allocatedAt <= value.serverTime, "Invalid nonce allocation");
const releaseSchema = z.object({ released: z.literal(true), serverTime: seconds });
const ownerSchema = z.object({ workerId, generation }).strict();
const allocationSchema = ownerSchema.extend({ requestId: workerId,
  size: z.number().int().min(1).max(100_000_000) }).strict();

export type MiningCurrentDto = z.infer<typeof miningCurrentSchema>;
export type MiningLeaseDto = z.infer<typeof miningLeaseSchema>;
export type MiningRangeDto = z.infer<typeof miningRangeSchema>;
export type MiningOwner = z.infer<typeof ownerSchema>;
export type MiningAllocationRequest = z.infer<typeof allocationSchema>;
export type MiningIdentity = { walletAddress: string; chainId: number; instanceId?: string };
type Challenge = z.infer<typeof sessionChallengeSchema>;
type Proof = z.infer<typeof sessionProofSchema>;

function invalidResponse(message: string): never {
  throw new ApiError(502, "INVALID_SERVER_RESPONSE", message, null, null);
}
function same(left: string, right: string): boolean { return left.toLowerCase() === right.toLowerCase(); }
function assertIdentity(challenge: Challenge, identity: MiningIdentity): void {
  if (!same(challenge.walletAddress, identity.walletAddress) || challenge.chainId !== identity.chainId ||
    identity.instanceId !== undefined && !same(challenge.instanceId, identity.instanceId))
    invalidResponse("Mining challenge identity mismatch");
}
function proofMatchesChallenge(proof: Proof, challenge: Challenge): boolean {
  return same(proof.challengeId, challenge.challengeId) && proof.difficultyEpochId === challenge.difficultyEpochId &&
    proof.targetExclusive === challenge.targetExclusive && proof.expiresAt === challenge.expiresAt &&
    proof.acceptedAt >= challenge.issuedAt;
}

/** Transport only: reads never acquire, transfer or start work. ApiClient owns cookie/revision fencing. */
export class MiningSessionApi {
  constructor(private readonly api: ApiClient) {}

  async current(worker: string, identity: MiningIdentity, signal?: AbortSignal): Promise<MiningCurrentDto> {
    const captured = { ...identity };
    const result = await this.api.get(`/mining/current?workerId=${encodeURIComponent(workerId.parse(worker))}`,
      miningCurrentSchema, signal);
    if (result.status === "LEGACY_CONFLICT") result.challenges.forEach(challenge => assertIdentity(challenge, captured));
    else if (result.status !== "NONE") assertIdentity(result.challenge, captured);
    return result;
  }

  async getChallenge(id: string, identity: MiningIdentity, signal?: AbortSignal): Promise<Challenge> {
    const captured = { ...identity };
    const result = await this.api.get(`/mining/challenges/${hex32Schema.parse(id)}`, sessionChallengeSchema, signal);
    if (!same(result.challengeId, id)) invalidResponse("Mining challenge ID mismatch");
    assertIdentity(result, captured);
    return result;
  }

  async create(tokenAddress: string, identity: MiningIdentity, signal?: AbortSignal): Promise<Challenge> {
    const captured = { ...identity };
    const result = await this.api.post("/mining/challenges", { tokenAddress: addressSchema.parse(tokenAddress) },
      sessionChallengeSchema, signal);
    assertIdentity(result, captured);
    if (!same(result.tokenAddress, tokenAddress)) invalidResponse("Mining source token mismatch");
    return result;
  }

  acquire(id: string, worker: string, takeover: boolean, signal?: AbortSignal): Promise<MiningLeaseDto> {
    return this.api.post(`/mining/challenges/${hex32Schema.parse(id)}/lease`,
      { workerId: workerId.parse(worker), takeover: z.boolean().parse(takeover) }, miningLeaseSchema, signal);
  }

  async heartbeat(id: string, owner: MiningOwner, signal?: AbortSignal): Promise<MiningLeaseDto> {
    const body = ownerSchema.parse(owner);
    const result = await this.api.post(`/mining/challenges/${hex32Schema.parse(id)}/lease/heartbeat`, body, miningLeaseSchema, signal);
    if (result.generation !== body.generation) invalidResponse("Heartbeat changed mining generation");
    return result;
  }

  release(id: string, owner: MiningOwner, signal?: AbortSignal) {
    return this.api.post(`/mining/challenges/${hex32Schema.parse(id)}/lease/release`,
      ownerSchema.parse(owner), releaseSchema, signal);
  }

  async allocate(id: string, request: MiningAllocationRequest, signal?: AbortSignal): Promise<MiningRangeDto> {
    // Caller adapts size to throughput/rejections and retains this exact payload for ambiguous retries.
    const body = allocationSchema.parse(request);
    const result = await this.api.post(`/mining/challenges/${hex32Schema.parse(id)}/nonce-ranges`, body, miningRangeSchema, signal);
    if (BigInt(result.endNonceExclusive) - BigInt(result.startNonce) !== BigInt(body.size))
      invalidResponse("Allocated nonce range size mismatch");
    return result;
  }

  async submit(challenge: Challenge, savedNonce: string, owner: MiningOwner, signal?: AbortSignal): Promise<Proof> {
    const captured = sessionChallengeSchema.parse(challenge);
    const body = { nonce: nonce.parse(savedNonce), ...ownerSchema.parse(owner) };
    const result = await this.api.post(`/mining/challenges/${captured.challengeId}/submit`,
      body, sessionProofSchema, signal);
    if (result.nonce !== body.nonce || !proofMatchesChallenge(result, captured)) invalidResponse("Submitted proof mismatch");
    return result;
  }

  async getProof(id: string, challenge: Challenge, signal?: AbortSignal): Promise<Proof> {
    const captured = sessionChallengeSchema.parse(challenge);
    const result = await this.api.get(`/mining/proofs/${hex32Schema.parse(id)}`, sessionProofSchema, signal);
    if (!same(result.proofId, id) || !proofMatchesChallenge(result, captured)) invalidResponse("Saved proof mismatch");
    return result;
  }
}

/** Conservative server clock for a single reply; no auth state, timers or renewal policy. */
export function miningResponseClock(serverTime: number, requestStartedAt: { monotonicAt: number; wallAt: number },
  monotonicNow: () => number = () => performance.now()): () => number {
  seconds.parse(serverTime);
  const receivedAt = monotonicNow();
  const receivedWall = Date.now();
  if (!Number.isFinite(requestStartedAt.monotonicAt) || !Number.isFinite(receivedAt) ||
    requestStartedAt.monotonicAt > receivedAt || !Number.isSafeInteger(requestStartedAt.wallAt))
    throw new RangeError("Invalid mining request timing");
  const transit = Math.max(0, receivedAt - requestStartedAt.monotonicAt, receivedWall - requestStartedAt.wallAt);
  const upperBound = serverTime * 1_000 + 1_000 + transit;
  if (!Number.isSafeInteger(receivedWall) || !Number.isFinite(upperBound) || upperBound > Number.MAX_SAFE_INTEGER)
    throw new RangeError("Invalid mining response timing");
  let lastNow = upperBound;
  return () => {
    const monotonicElapsed = monotonicNow() - receivedAt;
    const wallElapsed = Date.now() - receivedWall;
    const elapsed = Math.max(0, monotonicElapsed, wallElapsed);
    // A broken/rolled-back clock cannot restore expired authority.
    const next = upperBound + elapsed;
    lastNow = Number.isFinite(elapsed) && Number.isFinite(wallElapsed) && monotonicElapsed >= 0 &&
      Number.isFinite(monotonicElapsed) && next <= Number.MAX_SAFE_INTEGER ? Math.max(lastNow, next) : Infinity;
    return lastNow;
  };
}
